/**
 * TemplateManager - Helper class for managing HTML templates
 *
 * Provides methods to clone and populate templates from the DOM
 * keeping HTML out of JavaScript strings.
 */

import {t} from './translations.js';

class TemplateManager {
    constructor() {
        this.templates = window.substrateCalculatorTemplates;

        if (!this.templates) {
            console.error('SubstrateCalculator templates not found!');
            this.templates = {};
        }
    }

    /**
     * Clone a template by name
     *
     * @param {string} templateName - Name of the template to clone
     * @returns {DocumentFragment|null} Cloned template content
     */
    clone(templateName) {
        const template = this.templates[templateName];

        if (!template) {
            console.error(`Template "${templateName}" not found`);
            return null;
        }

        return template.content.cloneNode(true);
    }

    /**
     * Get shape icon SVG
     *
     * @param {string} shapeType - The shape type (box, pot, custom)
     * @returns {string|null} SVG HTML string
     */
    getShapeIcon(shapeType) {
        if (!this.templates.svgIcons) return null;

        const iconContainer = this.templates.svgIcons.querySelector(`[data-shape-icon="${shapeType}"]`);

        if (!iconContainer) return null;

        return iconContainer.innerHTML;
    }

    /**
     * Populate template with data
     *
     * @param {DocumentFragment} fragment - Template fragment to populate
     * @param {Object} data - Data to populate template with
     * @returns {DocumentFragment} Populated fragment
     */
    populate(fragment, data) {
        Object.keys(data).forEach(key => {
            const elements = fragment.querySelectorAll(`[data-substrate-calculator="${key}"]`);

            elements.forEach(element => {
                if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
                    element.value = data[key];
                } else {
                    element.innerHTML = data[key];
                }
            });
        });

        return fragment;
    }

    /**
     * Create an area card element
     *
     * @param {Object} areaData - Area data to display
     * @param {number} index - Area index
     * @param {Function} formatDimensionsText - Function to format dimensions
     * @param {Object} packagingVolumes - Actual packaging volumes to display
     * @returns {HTMLElement} Populated area card element
     */
    createAreaCard(areaData, index, formatDimensionsText, packagingVolumes = null) {
        const fragment = this.clone('areaCard');

        if (!fragment) return document.createElement('div');

        const card = fragment.querySelector('[data-substrate-calculator="area-card"]');
        const shapeIcon = this.getShapeIcon(areaData.shapeType || 'box');
        const dimensionsText = formatDimensionsText(areaData);
        const drainageText = areaData.drainageVolume > 0 ? 'TAK / z drenażem' : 'Bez drenażu';

        // Use packaging volumes if provided, otherwise calculate from area data
        let substrateDisplay, drainageDisplay, totalDisplay;

        if (packagingVolumes) {
            // Use actual packaging volumes (what customer will receive)
            substrateDisplay = Math.ceil(packagingVolumes.substrate);
            drainageDisplay = Math.ceil(packagingVolumes.drainage);
            totalDisplay = Math.ceil(packagingVolumes.total);
        } else {
            // Fallback: calculate from area data with safety buffer for substrate only
            const safetyMultiplier = 1 + ((packagingVolumes?.safetyBuffer || 20) / 100);
            const substrateWithBuffer = areaData.substrateVolume * safetyMultiplier;
            const drainageNoBuffer = areaData.drainageVolume; // No safety buffer for drainage
            const totalWithBuffer = substrateWithBuffer + drainageNoBuffer;

            substrateDisplay = Math.ceil(substrateWithBuffer);
            drainageDisplay = Math.ceil(drainageNoBuffer);
            totalDisplay = Math.ceil(totalWithBuffer);
        }

        // Populate card with data
        this.populate(fragment, {
            'area-title': `Obszar ${index + 1}`,
            'dimensions-text': dimensionsText,
            'drainage-text': drainageText,
            'total-volume': `${totalDisplay}l`,
            'substrate-volume': `${substrateDisplay}l`,
            'drainage-volume': `${drainageDisplay}l`,
            'shape-icon': shapeIcon || ''
        });

        // Hide drainage section if no drainage
        if (drainageDisplay <= 0) {
            const drainageVolumeEl = card.querySelector('[data-substrate-calculator="drainage-volume"]');
            if (drainageVolumeEl) {
                const container = drainageVolumeEl.parentElement;
                if (container) {
                    // Get all children to find and hide "+", drainage volume, and drainage label
                    const children = Array.from(container.children);
                    const substrateVolumeIndex = children.findIndex(el =>
                        el.getAttribute('data-substrate-calculator') === 'substrate-volume'
                    );

                    if (substrateVolumeIndex >= 0) {
                        // Hide everything after substrate volume and label (+, drainage volume, drainage label)
                        // substrate-volume, label, "+", drainage-volume, label
                        for (let i = substrateVolumeIndex + 2; i < children.length; i++) {
                            children[i].style.display = 'none';
                        }
                    }
                }
            }
        }

        // Set area index for edit button
        const editBtn = card.querySelector('[data-substrate-calculator="edit-area"]');
        if (editBtn) {
            editBtn.dataset.areaIndex = index;
        }

        return card;
    }

    /**
     * Create a product item element
     *
     * @param {Object} productData - Product data to display
     * @param {string} type - Product type (substrate/drainage)
     * @param {string} unit - Product unit (pallet/bag)
     * @param {number} quantity - Default quantity
     * @param {boolean} isLocked - Whether quantity is locked
     * @returns {HTMLElement} Populated product item element
     */
    createProductItem(productData, type, unit, quantity, isLocked) {
        const fragment = this.clone('productItem');

        if (!fragment) return document.createElement('div');

        const item = fragment.querySelector('[data-product-type]');

        // Determine labels based on type and unit
        const isDrainage = type === 'drainage';
        const isPallet = unit === 'pallet';

        let productName = productData.name;
        let productUrl = productData.url || '';
        let productDescription = productData.short_description || productData.description || '';
        let productId = productData.id || '';
        let productImage = productData.image || '';
        let price = productData.price || 0;
        let originalPrice = productData.original_price || price;
        let promotionEnabled = productData.promotion_enabled || false;
        let promotionPercentage = productData.promotion_percentage || 0;

        // Format price as plain text
        const currency = t('currency');
        let formattedPrice = price > 0 ? `${parseFloat(price).toFixed(2)} ${currency}` : `0 ${currency}`;
        let formattedOriginalPrice = originalPrice > 0 ? `${parseFloat(originalPrice).toFixed(2)} ${currency}` : '';

        // Set default labels if no product data
        if (!productName) {
            if (isPallet) {
                const packagingLabel = isDrainage ? t('big_bag') : t('pallet');
                const packagingVolume = isDrainage ? '2000l' : '1200l';
                const materialName = isDrainage ? t('expanded_clay').toLowerCase() : t('substrate').toLowerCase();
                productName = `${packagingLabel} ${materialName}`;
                productDescription = packagingVolume;
            } else {
                const materialName = isDrainage ? t('expanded_clay').toLowerCase() : t('substrate').toLowerCase();
                productName = `${t('bag')} ${materialName}`;
                productDescription = '50l';
            }
        }

        // Set item attributes
        item.setAttribute('data-product-type', type);
        item.setAttribute('data-product-unit', unit);
        // Store actual bag size for correct volume calculations
        const actualBagSize = productData.unit_price_factor || null;
        if (actualBagSize) {
            item.setAttribute('data-bag-size', actualBagSize);
        }

        // Update input attributes
        const qtyInput = item.querySelector('[data-substrate-calculator="product-qty"]');
        if (qtyInput) {
            qtyInput.setAttribute('data-product-type', type);
            qtyInput.setAttribute('data-product-unit', unit);
            qtyInput.setAttribute('data-product-id', productId);
            qtyInput.setAttribute('data-default-qty', quantity);
            qtyInput.value = quantity;

            if (isLocked) {
                qtyInput.setAttribute('readonly', '');
                qtyInput.classList.add('bg-gray-50', 'cursor-not-allowed');
            } else {
                qtyInput.removeAttribute('readonly');
                qtyInput.classList.remove('bg-gray-50', 'cursor-not-allowed');
            }
        }

        // Update buttons
        const minusBtn = item.querySelector('[data-qty-minus]');
        const plusBtn = item.querySelector('[data-qty-plus]');

        if (minusBtn) {
            minusBtn.setAttribute('data-locked', isLocked ? 'true' : 'false');
            if (isLocked) {
                minusBtn.setAttribute('disabled', '');
            } else {
                minusBtn.removeAttribute('disabled');
            }
        }

        if (plusBtn) {
            plusBtn.setAttribute('data-locked', isLocked ? 'true' : 'false');
            if (isLocked) {
                plusBtn.setAttribute('disabled', '');
            } else {
                plusBtn.removeAttribute('disabled');
            }
        }

        // Update product info
        const img = item.querySelector('[data-product-image]');
        const nameEl = item.querySelector('[data-product-name]');
        const descriptionEl = item.querySelector('[data-product-description]');

        if (img) {
            if (productImage) {
                img.src = productImage;
                img.alt = productName;
                img.classList.remove('hidden');
            } else {
                img.classList.add('hidden');
            }
        }

        if (nameEl) {
            if (productUrl) {
                // Create a link element
                const link = document.createElement('a');
                link.href = productUrl;
                link.textContent = productName;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.className = 'font-semibold text-brand-secondary-950 hover:text-brand-secondary-700 transition-colors';
                nameEl.innerHTML = '';
                nameEl.appendChild(link);
            } else {
                nameEl.textContent = productName;
            }
        }
        if (descriptionEl) descriptionEl.textContent = productDescription;

        // Update prices with promotion support - using data attributes
        const priceEl = item.querySelector('[data-product-price]');
        const originalPriceEl = item.querySelector('[data-product-original-price]');

        if (priceEl) {
            priceEl.setAttribute('data-product-price', price.toString());
        }

        if (originalPriceEl) {
            originalPriceEl.setAttribute('data-product-original-price', originalPrice.toString());
            originalPriceEl.setAttribute('data-product-promotion-enabled', promotionEnabled ? '1' : '0');
            originalPriceEl.setAttribute('data-product-promotion-percentage', promotionPercentage.toString());
        }

        return item;
    }
}

export default TemplateManager;
